/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";
import GeneralEntryExitDetailsApp
    from "../../../../../MaterialControl/Functionality/EntryAndExitForms/GeneralEntryExitDetailsApp";
import HandleChange from "../../../../../../../Helpers/HandleChange";
import {PHOTO_PICKER_MODEL} from "../../../../../../../components/PhotoPicker/SubComponent/Models/Models";
import {
    GATE_LOG_DISPATCH_FORMS,
    GATE_LOG_REGISTERED_VALUE_FIELDS,
    GATE_LOG_MODEL,
    getGateLogRegisteredTrailers,
    MATERIAL_CONTROL_REGISTERED_GATE_STATUS,
    showsGuardTransport
} from "../Model/Model";
import {
    MATERIAL_CONTROL_DETAIL_MODEL,
    MATERIAL_CONTROL_GATE_EVENT_TYPE,
    MATERIAL_CONTROL_PENDING_GATE_EVENT_TYPE,
    MATERIAL_CONTROL_REPORT_MODEL,
    MOVEMENT_DIRECTION,
    PLANT_MATERIAL_CONTROL_ITEM_MODEL,
    buildEntryAndExitFormItem,
    getMovementDirectionOption,
    getMovementDirectionOptionByValue,
    owesGateMovement,
    wasMaterialControlDispatched
} from "../../../../../MaterialControl/Model/Model";
import {
    CreateMaterialControlReport,
    RegisterActualQuantities,
    RegisterGateEvent,
    RegisterMaterialControlItemsBasic,
    RegisterMaterialControlTrailers,
    UpdateMaterialControlMovementValues,
    UpdateMaterialControlTransport
} from "../../../../../../../reduxToolkit/MaterialControl/MaterialControlApi";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {FILE_MODEL} from "../../../../../Visitor/Visitor/Model/Model";
import {errorMsg, isNotEmpty, sanitizeString} from "../../../../../../../Apis/functions";

/**
 * Detail of an entry and exit form read by the guard at the gate.
 *
 * The folio is loaded with the same service the requester reads it with, so
 * this layer only adds what the guard captures when the vehicle arrives: the
 * gate log of the movement and the actions that close it.
 *
 * @returns {Object} Information of the folio and handlers of the gate log.
 */
const GuardEntryAndExitFormDetailsApp = () => {
    const dispatch = useDispatch();
    const {user} = useSelector((state) => state.authenticatioauthennSlice);
    // Every action of the gate is registered for the guard that performs it
    const userId = user?.data?.id;
    const details = GeneralEntryExitDetailsApp();
    const [gateLog, setGateLog] = useState({...GATE_LOG_MODEL});
    const [photoPicker, setPhotoPicker] = useState({...PHOTO_PICKER_MODEL});
    const [emptyFields, setEmptyFields] = useState(false);
    const [emptyFieldsItems, setEmptyFieldsItems] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [modified, setModified] = useState(false);
    const {handleChange} = HandleChange({model: gateLog, setModel: setGateLog});

    /**
     * Reads the folio again once the delivery of a material is registered.
     *
     * The materials are validated one by one from the list, so the folio is
     * loaded again to display what is already delivered and what is left.
     */
    useEffect(() => {
        if (!modified) return;

        details?.getInfo();
        setModified(false);
    }, [modified]);

    /**
     * Opens the log with the evidence of the last movement registered at the
     * gate, so a folio that is only read displays the files the guard attached
     * when the movement was written.
     *
     * A folio the gate already closed writes nothing else, so its log is opened
     * with the notes of that movement too; one that is still waiting keeps the
     * notes the guard is writing on it.
     */
    useEffect(() => {
        if (!details?.item) return;

        const lastGateEvent = (details.item.gateEvents || []).slice(-1)[0] || null;
        // A folio reported with discrepancies is read with the details of its
        // dispatch, so its log is the one the dispatch wrote: the movement
        // registered before the report, such as the arrival of the vehicle,
        // has a log of its own and is not read as the dispatch
        const loggedEvent = details.item.dispatchStatusId === STATUS_MODEL.DISCREPANCY
            ? MATERIAL_CONTROL_DETAIL_MODEL.getDispatchEvent(details.item)
            : lastGateEvent;
        // A folio reported with discrepancies is not written by the gate any
        // more, so its log is read from the movement the guard already
        // registered, the same way a closed one is; a folio that already
        // dispatched its materials reads it too, because what the gate wrote
        // for the dispatch is what its log displays.
        //
        // A folio that still waits for a movement of the gate writes a log of
        // its own, so what was registered before is not read into it: the
        // departure is written with its own notes, its own date and its own
        // evidence, and the ones of the entry stay as they were registered.
        //
        // A folio that still owes a movement of the gate has not written its
        // log yet, so it is opened clean even when it was reported: the report
        // does not replace the movement it was waiting for, and the log of the
        // one registered before it is not read as its own.
        const isRegistered = (MATERIAL_CONTROL_REGISTERED_GATE_STATUS
                .includes(details.item.dispatchStatusId)
                || details.item.dispatchStatusId === STATUS_MODEL.DISCREPANCY
                || wasMaterialControlDispatched(details.item))
            && !MATERIAL_CONTROL_PENDING_GATE_EVENT_TYPE[details.item.dispatchStatusId]
            && !owesGateMovement(details.item);

        // The date of the movement travels with the movement the gate wrote, so
        // a folio that was already registered reads it back into the field of
        // the direction it takes and displays what the guard captured
        const movementDateName = (getMovementDirectionOptionByValue(details.item.movementDirection)
            || getMovementDirectionOption(MOVEMENT_DIRECTION.EXIT)).dateName;

        // A folio that waits for a movement of the gate registers it with the
        // moment it happens, so the date of the movement is not read from the
        // one the folio expected: the arrival and the departure are written
        // with what the guard sees at the gate
        const writesGateMovement = !!MATERIAL_CONTROL_PENDING_GATE_EVENT_TYPE[details.item.dispatchStatusId];

        // Every movement the gate registers is written with a log of its own,
        // so a folio that is still open opens it clean: what was captured for
        // the movement before does not travel with the next one, and the
        // dispatch is not written with what the arrival was closed with
        const startsGateLog = !isRegistered;

        // The date of the movement is optional for the requester, so the one
        // that travels with the folio opens the log of the gate and the guard
        // only confirms it or writes the one the movement happened on
        const expectedDate = details.item.expectedMovementDate
            ? new Date(details.item.expectedMovementDate)
            : null;

        // The materials of the movement are written in the log of the gate, so
        // the ones the folio already carries are read there and not only the
        // empty material the log opens with. A material that was registered
        // travels with the id it was stored under, because it is only read and
        // is not written again when the movement is closed.
        const registeredItems = (details.item.items || []).map((e) => ({
            ...buildEntryAndExitFormItem(PLANT_MATERIAL_CONTROL_ITEM_MODEL),
            ...(e?.id != null ? {id: e.id, registeredId: e.id} : {}),
            name: e?.name ?? '',
            type: e?.type ?? null
        }));

        /**
         * Materials the log of the gate is opened with.
         *
         * The materials are written by whoever registers them first: a folio
         * that travels with them is only read at the gate, and one that was
         * registered without them is the one the guard writes at the gate.
         *
         * @param {Array} written - Materials the log already holds.
         *
         * @returns {Array} Materials the log is read with.
         */
        const getItems = (written) => registeredItems.length > 0 ? registeredItems : written;

        // The values the requester can leave blank travel with the folio when
        // they were captured, so the log of the gate is opened with them: those
        // are the ones the guard only reads, and the rest are the ones written
        // at the gate
        const registeredValues = GATE_LOG_REGISTERED_VALUE_FIELDS
            .reduce((acc, name) => {
                if (isNotEmpty(details.item[name])) acc[name] = details.item[name];

                return acc;
            }, {});

        setGateLog((prev) => ({
            ...prev,
            ...registeredValues,
            // The trailers are records of their own, so the log is opened with
            // the ones the folio already registered and they travel back as
            // they were when the gate completes the rest
            ...getGateLogRegisteredTrailers(details.item),
            notes: isRegistered
                ? sanitizeString(loggedEvent?.notes) || ''
                : '',
            // The date of the movement is the one the folio declares, and not
            // the moment the gate registered it, so a folio that was already
            // closed reads it back the same way it was written
            [movementDateName]: isRegistered
                ? (expectedDate ?? (loggedEvent?.eventDate ? new Date(loggedEvent.eventDate) : null))
                : (writesGateMovement ? null : (prev[movementDateName] ?? expectedDate)),
            evidence: isRegistered
                ? (loggedEvent?.evidence || [])
                    .filter((e) => e?.fileUploadDto)
                    .map((e) => FILE_MODEL.set(e))
                : [],
            items: getItems(prev.items)
        }));

        if (startsGateLog) setPhotoPicker({...PHOTO_PICKER_MODEL});
    }, [details?.item?.id, details?.item?.dispatchStatusId, details?.item?.gateEvents?.length]);

    /**
     * Tells whether the movement can be closed with the dispatch.
     *
     * The notes and the evidence of what arrives at the plant travel with the
     * dispatch, so the guard is warned about what is left blank before the
     * movement is closed. The type of the form tells which fields it is closed
     * with, so what the requester left to the gate is validated with it.
     *
     * Once the arrival is registered the materials are validated one by one
     * from the list, so the evidence of every one of them is asked for along
     * with the log the movement is closed with.
     *
     * @returns {boolean} True when what closes the movement is complete.
     */
    const validFields = () => {
        const statusId = details?.item?.dispatchStatusId;
        // A folio that waits for a movement of the gate is closed with the log
        // of that movement, so what the dispatch writes is not asked for yet:
        // the arrival and the departure only carry what the guard saw
        const pendingGateEvent = MATERIAL_CONTROL_PENDING_GATE_EVENT_TYPE[statusId];

        // The log is asked for when a movement of the gate is what the folio
        // waits for, and by the forms whose log is written by the dispatch
        // itself; the rest are dispatched with the materials already validated
        const writesLogOnDispatch = GATE_LOG_DISPATCH_FORMS.includes(details?.form?.key);
        const validGateLog = (!pendingGateEvent && !writesLogOnDispatch)
            || gateLog?.isValid({
                photoPicker,
                form: details?.form,
                item: details?.item,
                dispatchLog: !pendingGateEvent,
                // The transport is written while the vehicle is at the gate, so
                // it is asked for by the log that displays it: the arrival of
                // the unit when the folio waits for it, and the dispatch when
                // the folio goes straight to it
                transportLog: showsGuardTransport(details?.item)
            });

        // The materials are validated one by one before the folio is
        // dispatched, so the movement the gate registers on its own closes with
        // the ones that were already handed over.
        //
        // The forms whose log is written by the dispatch write their materials
        // in that log, so they are not handed over one by one and the evidence
        // of every one of them is not asked for.
        const validItems = !!pendingGateEvent
            || writesLogOnDispatch
            || (details?.item?.items || []).every((e) => e?.itemEvidence != null);

        // The materials the gate writes are marked on their own, because the
        // movement closes with none of them: a log that is missing its notes or
        // its evidence says nothing about the material that was left blank
        const validGateMaterials = !!pendingGateEvent
            || !!gateLog?.isValidGateMaterials(details?.form);

        const valid = validGateLog && validItems;

        setEmptyFields(!valid);
        setEmptyFieldsItems(!validGateMaterials);

        if (!valid) toast.warn(details?.t('please_fill_all_fields'));

        return valid;
    };

    /**
     * Registers a movement of the gate with the log the guard captures.
     *
     * The arrival of the vehicle, the dispatch of the material and the
     * departure that takes it out of the plant are written the same way, and
     * the folio is read again so the detail displays what was registered.
     *
     * @param {string} type - Movement the gate registers.
     * @param {Array} files - Evidence of the movement captured at the gate.
     * @param {string} [successTrans] - Notice the movement is confirmed with.
     *
     * @returns {void}
     */
    const registerGateMovement = (type, files, successTrans = 'has_been_created_successfully') => {
        dispatch(RegisterGateEvent(gateLog.toRegister({
            materialControlId: details?.item?.id,
            type,
            files,
            userId
        }))).then(({payload: {data: {success, code}}}) => {
            if (success) {
                toast.success(details?.t(successTrans));
                setEmptyFields(false);
            } else errorMsg(code);

            // The dispatch closes the folio before its log is written, so the
            // folio is read again either way and the detail displays the status
            // it was left in
            details?.getInfo();
        });
    };

    /**
     * Registers the dispatch that closes the materials of the folio.
     *
     * The dispatch is the movement that hands the material over, so it leaves
     * the folio in the status that says so and writes its own log at the gate:
     * that is where the guard that registered it, the moment it was registered
     * and what was captured with it are read from, the same way the arrival and
     * the departure are read.
     *
     * The log of the dispatch is the one that leaves the folio in its status,
     * so the status and the log are written together by the gate.
     *
     * @returns {void}
     */
    const registerDispatch = () => {
        registerGateLogValues().then((registered) => registered && registerGateMovement(
            MATERIAL_CONTROL_GATE_EVENT_TYPE[STATUS_MODEL.DISPATCHED],
            photoPicker?.listFiles,
            'has_been_dispatched_successfully'
        ));
    };

    /**
     * Registers the transport the guard completed while the vehicle was at the
     * gate.
     *
     * The vehicle is read the moment it reaches the plant, so the folio that
     * waits for its arrival writes the transport with that movement and not with
     * the dispatch that closes it later. Nothing is sent when the guard wrote
     * nothing, and the forms that leave no block to the gate write none.
     *
     * @returns {Promise<boolean>} Resolves with whether the transport was
     * registered, true when there was nothing to send.
     */
    const registerGateTransport = () => {
        if (!GATE_LOG_DISPATCH_FORMS.includes(details?.form?.key))
            return Promise.resolve(true);

        const transport = gateLog?.toRegisterTransport(details?.item, userId);

        if (!transport)
            return Promise.resolve(true);

        return dispatch(UpdateMaterialControlTransport(transport))
            .then((answer) => registeredAll([answer]));
    };

    /**
     * Registers the values the gate writes on the blocks of the folio.
     *
     * The materials that came into the plant or left it and the transport the
     * requester left blank live in blocks of their own, so they are registered
     * before the dispatch closes the folio; the forms that leave nothing to the
     * gate write none of them.
     *
     * The date of the movement is written on the folio itself, so it is only
     * sent when the requester left it blank: the folio keeps a single date and
     * its history says who wrote it.
     *
     * @returns {Promise<boolean>} Resolves with whether every block was
     * registered, true when there was nothing to send.
     */
    const registerGateLogValues = () => {
        if (!GATE_LOG_DISPATCH_FORMS.includes(details?.form?.key))
            return Promise.resolve(true);

        const materialControlId = details?.item?.id;
        const items = gateLog?.toRegisterItems(userId) || [];
        const transport = gateLog?.toRegisterTransport(details?.item, userId);
        // The service counts every packaging in the same call, so the quantities
        // the gate wrote travel together
        const packagingQuantities = gateLog?.toRegisterPackagingQuantities(details?.item, userId) || [];
        // The service writes every trailer in the same call, so the ones the
        // gate completed travel together, named by the position they are pulled in
        const trailers = gateLog?.toRegisterTrailers(details?.item, userId) || [];
        // The date, the platforms and the observations are written on the folio
        // itself, so the ones the gate completed travel in a single update and
        // the history says who wrote them
        const movementValues = gateLog?.toRegisterMovementValues(details?.item, userId);
        const calls = [];

        if (transport)
            calls.push(dispatch(UpdateMaterialControlTransport(transport)));

        if (movementValues)
            calls.push(dispatch(UpdateMaterialControlMovementValues(movementValues)));

        if (items.length > 0)
            calls.push(dispatch(RegisterMaterialControlItemsBasic({id: materialControlId, body: items}))
                .then((answer) => {
                    // A material already stored is only read from then on, so
                    // the retry after another value was turned down does not
                    // write it twice
                    if (answer?.payload?.data?.success === true) markItemsRegistered();

                    return answer;
                }));

        if (packagingQuantities.length > 0)
            calls.push(dispatch(RegisterActualQuantities(packagingQuantities)));

        if (trailers.length > 0)
            calls.push(dispatch(RegisterMaterialControlTrailers(trailers)));

        return Promise.all(calls).then((answers) => {
            const registered = registeredAll(answers);

            // The values that were stored before one was turned down are read
            // again from the folio, so the retry sends only what is missing
            if (!registered) details?.getInfo();

            return registered;
        });
    };

    /**
     * Marks the materials the gate wrote as stored on the folio.
     *
     * The same materials {@link GATE_LOG_MODEL.toRegisterItems} sends are the
     * ones marked, so the log reads them as registered and does not send them
     * again.
     *
     * @returns {void}
     */
    const markItemsRegistered = () => setGateLog((prev) => ({
        ...prev,
        items: (prev.items || []).map((e) => !e?.registeredId && isNotEmpty(e?.name) && e?.type !== null
            ? {...e, registeredId: true}
            : e)
    }));

    /**
     * Reads the answers of the values the gate registered before a movement.
     *
     * The movement closes what the guard captured, so it is only written once
     * every value was registered: the first one turned down is the notice the
     * guard reads, and the movement is not sent.
     *
     * @param {Array} answers - Answers of the calls, empty when nothing was sent.
     *
     * @returns {boolean} Whether every value was registered.
     */
    const registeredAll = (answers) => {
        const failed = answers.find(({payload}) => payload?.data?.success !== true);

        if (failed) errorMsg(failed?.payload?.data?.code ?? failed?.payload?.status);

        return !failed;
    };

    /**
     * Raises the report of the discrepancies found at the gate.
     *
     * A folio closed with a discrepancy is not dispatched: the report is what
     * carries what the guard found, and the folio is read again so the badge of
     * the detail displays it.
     *
     * The report is an action of its own, so it only sends the discrepancy:
     * the values the guard captured are registered with the movement that
     * closes the gate, not with the report.
     *
     * @param {string} comment - Discrepancy the folio is closed with.
     * @param {Array} [files] - Evidence of the discrepancy captured at the gate.
     *
     * @returns {void}
     */
    const registerReport = (comment, files) => {
        dispatch(CreateMaterialControlReport(MATERIAL_CONTROL_REPORT_MODEL.toRegister({
            materialControlId: details?.item?.id,
            comment,
            files,
            userId
        }))).then(({payload: {data: {success, code}}}) => {
            if (!success) return errorMsg(code);

            toast.success(details?.t('has_been_reported_successfully'));
            setEmptyFields(false);
            closeModal();
            details?.getInfo();
        });
    };

    /**
     * Closes what the folio is waiting for at the gate.
     *
     * A folio that waits for the vehicle is closed with the movement the gate
     * registers — the arrival that lets it be dispatched or the departure that
     * takes it out of the plant — and one that only waits for its materials is
     * closed with the dispatch, which writes no movement of its own.
     *
     * @returns {void}
     */
    const handleDispatch = () => {
        if (!validFields()) return;

        const gateEventType = MATERIAL_CONTROL_PENDING_GATE_EVENT_TYPE[details?.item?.dispatchStatusId];

        if (gateEventType)
            // The transport is completed when the vehicle is at the gate, so it
            // is written before the movement that registers it
            return registerGateTransport()
                .then((registered) => registered && registerGateMovement(gateEventType, photoPicker?.listFiles));

        registerDispatch();
    };

    /**
     * Opens the report of the discrepancies found at the gate.
     *
     * The discrepancy is what closes the movement, so the report only asks for
     * it and the gate log stays as the guard left it.
     *
     * @returns {void}
     */
    const openReportModal = () => {
        setShowConfirmModal(true);
    };

    /**
     * Closes the confirmation of the report.
     *
     * @returns {void}
     */
    const closeModal = () => {
        setShowConfirmModal(false);
    };

    /**
     * Registers the report of the discrepancies confirmed by the guard.
     *
     * The discrepancy is the reason the movement is closed with, and the
     * evidence of the report is the one captured at the gate, so both travel
     * with the report the guard confirms.
     *
     * @param {string} dispatchReason - Discrepancy the movement is closed with.
     * @param {Array} [files] - Evidence of the discrepancy captured at the gate.
     *
     * @returns {void}
     */
    const handleActionModal = (dispatchReason, files) => {
        registerReport(dispatchReason, files);
    };

    return {
        ...details,
        gateLog,
        setGateLog,
        handleChange,
        photoPicker,
        setPhotoPicker,
        emptyFields,
        setEmptyFields,
        emptyFieldsItems,
        setEmptyFieldsItems,
        setModified,
        showConfirmModal,
        handleDispatch,
        openReportModal,
        handleActionModal,
        closeModal
    };
};

export default GuardEntryAndExitFormDetailsApp;
